import React from "react";
import PropTypes from "prop-types";
import { TextField, MenuItem, InputAdornment } from "@mui/material";
import { colors, customShadows, borderRadiusPx, fontFamilies } from "../../../../theme";

/**
 * CommonTextField Component
 * Centralized, reusable text field & select input adhering to the AutoBET theme palette and tokens.
 * Visually consistent with CommonInput (cardFlat shadow, 12px borderRadius, border, and focus glow).
 *
 * Supports:
 * - Standard input types (text, number, email, password, etc.)
 * - Select dropdowns via `select` prop + `options` array (or `children` MenuItem elements)
 * - Read-only styling, custom adornments, and sizes ('small' | 'medium')
 */
const CommonTextField = ({
  label,
  value,
  onChange,
  placeholder,
  select = false,
  options = [],
  children,
  size = "small",
  fullWidth = true,
  variant = "outlined",
  disabled = false,
  readOnly = false,
  error = false,
  helperText,
  startAdornment,
  endAdornment,
  textAlign,
  inputProps = {},
  InputProps = {},
  InputLabelProps = {},
  SelectProps = {},
  sx = {},
  ...props
}) => {
  const resolvedStartAdornment = startAdornment
    ? React.isValidElement(startAdornment) && startAdornment.type === InputAdornment
      ? startAdornment
      : <InputAdornment position="start">{startAdornment}</InputAdornment>
    : InputProps.startAdornment;

  const resolvedEndAdornment = endAdornment
    ? React.isValidElement(endAdornment) && endAdornment.type === InputAdornment
      ? endAdornment
      : <InputAdornment position="end">{endAdornment}</InputAdornment>
    : InputProps.endAdornment;

  const mergedInputProps = {
    readOnly,
    startAdornment: resolvedStartAdornment,
    endAdornment: resolvedEndAdornment,
    ...InputProps,
    sx: {
      borderRadius: borderRadiusPx.xl,
      fontSize: "14px",
      height: size === "small" ? "40px" : "48px",
      minHeight: size === "small" ? "40px" : "48px",
      display: "flex",
      alignItems: "center",
      backgroundColor: disabled
        ? colors.slate[100]
        : readOnly
        ? colors.slate[50]
        : colors.background.paper,
      color: colors.text.primary,
      boxShadow: customShadows.cardFlat,
      transition: "all 0.2s ease-in-out",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: error ? colors.error?.main || "#ef4444" : colors.neutral.border,
        borderRadius: borderRadiusPx.xl,
        transition: "all 0.2s ease-in-out",
        ...(!label ? { top: "0 !important", "& legend": { display: "none !important" } } : {}),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: error
          ? colors.error?.dark || "#dc2626"
          : colors.neutral.borderHover || colors.slate[300],
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: error
          ? colors.error?.main || "#ef4444"
          : colors.primary.main,
      },
      "& input, & textarea, & .MuiSelect-select": {
        fontSize: "14px !important",
        fontFamily: "inherit !important",
        color: `${colors.text.primary} !important`,
        border: "none !important",
        outline: "none !important",
        boxShadow: "none !important",
        background: "transparent !important",
        boxSizing: "border-box !important",
        "&:focus": {
          border: "none !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
      },
      "& input": {
        height: "100% !important",
        py: "0 !important",
        pt: "0 !important",
        pb: "0 !important",
        lineHeight: "normal !important",
        boxSizing: "border-box !important",
        display: "block !important",
        alignSelf: "center",
        ...(textAlign ? { textAlign: `${textAlign} !important` } : {}),
      },
      "& input[type=number]": {
        MozAppearance: "textfield",
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
      },
      "& .MuiInputAdornment-root": {
        height: "100% !important",
        maxHeight: "none !important",
        display: "inline-flex !important",
        alignItems: "center !important",
        justifyContent: "center !important",
        my: "auto !important",
        "& p, & span, & .MuiTypography-root": {
          fontSize: "14px !important",
          lineHeight: "1 !important",
          margin: "0 !important",
          padding: "0 !important",
          display: "inline-flex !important",
          alignItems: "center !important",
          color: colors.slate[500],
        },
      },
      "& .MuiSelect-select": {
        height: "100% !important",
        minHeight: "100% !important",
        py: "0 !important",
        pt: "0 !important",
        pb: "0 !important",
        display: "flex !important",
        alignItems: "center !important",
        boxSizing: "border-box !important",
        lineHeight: "normal !important",
      },
      "& textarea": {
        lineHeight: "1.5 !important",
      },
      ...(InputProps?.sx || {}),
    },
  };

  const mergedInputLabelProps = {
    shrink: InputLabelProps?.shrink !== undefined ? InputLabelProps.shrink : true,
    ...InputLabelProps,
    sx: {
      fontSize: "14px",
      fontFamily: fontFamilies.primary,
      color: error ? colors.error?.main || "#ef4444" : colors.slate[600],
      "&.Mui-focused": {
        color: error ? colors.error?.main || "#ef4444" : colors.primary.main,
      },
      "&.MuiInputLabel-shrink": {
        fontWeight: 600,
        fontFamily: fontFamilies.primary,
      },
      ...(InputLabelProps?.sx || {}),
    },
  };

  const mergedSelectProps = select
    ? {
        MenuProps: {
          disableScrollLock: true,
          PaperProps: {
            sx: {
              borderRadius: borderRadiusPx.xl,
              boxShadow: customShadows.dropdownMenu || "0 10px 30px rgba(0, 0, 0, 0.12)",
              border: `1px solid ${colors.dashboard.cardBorder}`,
              mt: 0.75,
              p: 0.5,
              "& .MuiMenuItem-root": {
                fontSize: "14px",
                fontWeight: 500,
                color: colors.slate[700],
                py: 1,
                px: 1.5,
                my: 0.25,
                borderRadius: borderRadiusPx.md,
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  backgroundColor: colors.slate[100],
                  color: colors.slate[900],
                },
                "&.Mui-selected": {
                  backgroundColor: colors.blue[50],
                  color: colors.primary.main,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: colors.blue[100],
                  },
                },
              },
            },
          },
          ...(SelectProps?.MenuProps || {}),
        },
        ...SelectProps,
      }
    : SelectProps;

  return (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      select={select}
      size={size}
      fullWidth={fullWidth}
      variant={variant}
      disabled={disabled}
      error={error}
      helperText={helperText}
      inputProps={{
        ...(textAlign ? { style: { textAlign, ...(inputProps?.style || {}) } } : {}),
        ...inputProps,
      }}
      InputProps={mergedInputProps}
      InputLabelProps={mergedInputLabelProps}
      SelectProps={mergedSelectProps}
      sx={{
        "& .MuiFormHelperText-root": {
          fontSize: "0.75rem",
        },
        ...sx,
      }}
      {...props}
    >
      {select && options && options.length > 0
        ? options.map((option) => {
            const optValue = typeof option === "object" ? option.value : option;
            const optLabel = typeof option === "object" ? option.label : option;
            const optDisabled = typeof option === "object" ? option.disabled : false;
            return (
              <MenuItem key={optValue} value={optValue} disabled={optDisabled}>
                {optLabel}
              </MenuItem>
            );
          })
        : children}
    </TextField>
  );
};

CommonTextField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  select: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.node.isRequired,
        disabled: PropTypes.bool,
      }),
    ])
  ),
  children: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  InputProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  SelectProps: PropTypes.object,
  sx: PropTypes.object,
};

export default CommonTextField;
